import React, { type JSX } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
/**
 * An entry of DocumentPreviewSlice's openDocuments. Only the fields the
 * workspace itself reads are named; the preview panel takes the whole object.
 */
export interface OpenDocument {
  docId?: string;
  researchTitle?: string;
  originalFilename?: string;
}

/** A document preview tab: the open document plus its resolved display name. */
export interface DocumentTab {
  id: string;
  name: string;
  document: OpenDocument;
}

export type TabBarVariant = "mobile" | "desktop";

/**
 * Mobile packs the tabs tighter and truncates labels sooner; the deal doc count
 * badge is desktop-only because the mobile strip has no room for it.
 */
const VARIANTS: Record<TabBarVariant, { pad: string; labelWidth: string; dealDocsCount: boolean }> =
  {
    mobile: { pad: "py-3 px-4", labelWidth: "max-w-[150px]", dealDocsCount: false },
    desktop: { pad: "py-2 px-5", labelWidth: "max-w-[200px]", dealDocsCount: true },
  };

const ACTIVE_TAB = "border-b-2 border-cvc-blue text-cvc-blue";
const INACTIVE_TAB = "text-cvc-text hover:text-cvc-blue border-b-2 border-transparent";

/** Tab button shared by every tab flavour; `active` drives the underline. */
function TabButton({
  active,
  pad,
  gap,
  title,
  onClick,
  children,
}: {
  active: boolean;
  pad: string;
  gap?: boolean;
  title?: string;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`relative inline-flex items-center ${gap ? "gap-1.5 " : ""}${pad} text-sm font-cvcBody font-normal focus:outline-none transition-colors duration-200 ease-in-out whitespace-nowrap ${
        active ? ACTIVE_TAB : INACTIVE_TAB
      }`}
    >
      {children}
    </button>
  );
}

/** Count pill shown ahead of a tab label. */
function CountBadge({ count }: { count: number }): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <span className="inline-flex items-center justify-center rounded-full bg-cvc-blue/10 text-cvc-blue text-xs font-normal px-1.5 h-[20px] min-w-[20px]">
      {count}
    </span>
  );
}

/**
 * The close cross nested inside a tab button. It is a span with a button role
 * because HTML forbids a button inside a button.
 */
function CloseAffordance({ label, onClose }: { label: string; onClose: () => void }): JSX.Element {
  const close = (event: React.SyntheticEvent): void => {
    event.stopPropagation();
    onClose();
  };
  return (
    <span
      onClick={close}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          close(e);
        }
      }}
      role="button"
      tabIndex={0}
      className="ml-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
      aria-label={label}
    >
      <XMarkIcon className="h-3 w-3" />
    </span>
  );
}

/** Deal name header plus the Summary / Docs pair, shown while a deal is open. */
function DealTabs({
  variant,
  dealName,
  dealDocsCount,
  activeTab,
  onSelectDealTab,
  onCloseDeal,
}: {
  variant: TabBarVariant;
  dealName: string;
  dealDocsCount: number;
  activeTab: string;
  onSelectDealTab: (tab: "summary" | "docs") => void;
  onCloseDeal: () => void;
}): JSX.Element {
  const { pad, labelWidth, dealDocsCount: showCount } = VARIANTS[variant];
  return (
    <>
      <span
        className={`inline-flex items-center gap-1.5 ${pad} text-sm font-cvcBody font-semibold text-cvc-text truncate ${labelWidth}`}
      >
        {dealName}
      </span>
      <TabButton
        active={activeTab === "DealSummary"}
        pad={pad}
        onClick={() => onSelectDealTab("summary")}
      >
        Summary
      </TabButton>
      <TabButton
        active={activeTab === "DealDocs"}
        pad={pad}
        gap
        onClick={() => onSelectDealTab("docs")}
      >
        {showCount && <CountBadge count={dealDocsCount} />}
        Docs
        <CloseAffordance label="Close deal details" onClose={onCloseDeal} />
      </TabButton>
    </>
  );
}

export interface WorkspaceTabBarProps {
  hasDealSelected: boolean;
  dealName: string;
  dealDocsCount: number;
  activeTab: string;
  baseTabs: string[];
  documentTabs: DocumentTab[];
  getTabCount: (tab: string) => number;
  onSelectTab: (tab: string) => void;
  onSelectDealTab: (tab: "summary" | "docs") => void;
  onCloseDeal: () => void;
  onCloseDocument: (docId: string) => void;
}

/** The workspace tab strip: deal tabs or base tabs, then one tab per preview. */
export default function WorkspaceTabBar({
  variant,
  tabs,
}: {
  variant: TabBarVariant;
  tabs: WorkspaceTabBarProps;
}): JSX.Element {
  const { pad, labelWidth } = VARIANTS[variant];
  return (
    <>
      {tabs.hasDealSelected ? (
        <DealTabs
          variant={variant}
          dealName={tabs.dealName}
          dealDocsCount={tabs.dealDocsCount}
          activeTab={tabs.activeTab}
          onSelectDealTab={tabs.onSelectDealTab}
          onCloseDeal={tabs.onCloseDeal}
        />
      ) : (
        tabs.baseTabs.map((tab) => (
          <TabButton
            key={tab}
            active={tabs.activeTab === tab}
            pad={pad}
            gap
            onClick={() => tabs.onSelectTab(tab)}
          >
            <CountBadge count={tabs.getTabCount(tab)} />
            <span className={`truncate ${labelWidth}`}>{tab}</span>
          </TabButton>
        ))
      )}
      {tabs.documentTabs.map((docTab) => (
        <TabButton
          key={docTab.id}
          active={tabs.activeTab === docTab.name}
          pad={pad}
          title={docTab.name}
          onClick={() => tabs.onSelectTab(docTab.name)}
        >
          <span className={`truncate ${labelWidth}`}>{docTab.name}</span>
          <CloseAffordance
            label="Close document preview"
            onClose={() => tabs.onCloseDocument(docTab.id)}
          />
        </TabButton>
      ))}
    </>
  );
}
